import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import oidcConfig from '../oidc-config.ts';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const API_KEY = process.env.REACT_APP_API_KEY;
const IS_DEV = process.env.NODE_ENV === 'development';

/**
 * Create a configured axios instance with authentication interceptors
 */
export function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - Add authentication headers
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Try to get OIDC token from sessionStorage (primary method)
      const oidcStorage = sessionStorage.getItem(`oidc.user:${oidcConfig.authority}:${oidcConfig.client_id}`);

      if (oidcStorage) {
        try {
          const oidcUser = JSON.parse(oidcStorage);
          if (oidcUser && oidcUser.access_token) {
            config.headers.Authorization = `Bearer ${oidcUser.access_token}`;
            if (IS_DEV) console.log('✅ OIDC token added to request');
          }
        } catch (e) {
          if (IS_DEV) console.error('❌ Error parsing OIDC user:', e);
        }
      }

      // Fallback to localStorage authToken (legacy method)
      const token = localStorage.getItem('authToken');
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
        if (IS_DEV) console.log('✅ Auth token added to request');
      }

      // Add API key if configured
      if (API_KEY) {
        config.headers['X-API-Key'] = API_KEY;
        if (IS_DEV) console.log('✅ API Key added to request');
      } else {
        if (IS_DEV) console.warn('⚠️ No API Key configured');
      }

      // Debug: Log request details
      if (IS_DEV) {
        if (!config.headers.Authorization) {
          console.warn('⚠️ No Authorization token found!');
        }

        console.log('📤 API Request:', {
          url: `${config.baseURL}${config.url}`,
          method: config.method,
          hasAuth: !!config.headers.Authorization,
          hasApiKey: !!config.headers['X-API-Key']
        });
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - Handle errors and provide debugging info
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (IS_DEV) {
        if (error.response?.status === 401) {
          console.error('❌ 401 Unauthorized - Authentication failed:');
          console.error('  - Are you logged in?');
          console.error('  - Check if JWT token exists in sessionStorage');
          console.error('  - Token might be expired - try logging in again');
          console.error('  - Backend response:', error.response.data);

          // Check if token exists
          const oidcStorage = sessionStorage.getItem(`oidc.user:${oidcConfig.authority}:${oidcConfig.client_id}`);
          if (!oidcStorage) {
            console.error('  ❌ No OIDC token found in sessionStorage!');
            console.error('  → Please login first');
          } else {
            try {
              const oidcUser = JSON.parse(oidcStorage);
              const tokenExp = oidcUser.expires_at;
              const now = Math.floor(Date.now() / 1000);
              if (tokenExp && tokenExp < now) {
                console.error('  ❌ Token is expired!');
                console.error('  → Please login again');
              } else {
                console.error('  ✅ Token exists and not expired');
                console.error('  → Backend might be rejecting the token');
                console.error('  → Check backend JWT validation');
              }
            } catch (e) {
              console.error('  ❌ Error parsing token:', e);
            }
          }
        } else if (error.response?.status === 403) {
          console.error('❌ 403 Forbidden - Check your authentication:');
          console.error('  - Is your JWT token valid?');
          console.error('  - Is your API key correct?');
          console.error('  - Does your user have permission?');
          console.error('  - Backend response:', error.response.data);
        } else if (error.response) {
          console.error(`❌ HTTP ${error.response.status} Error:`, error.response.data);
        } else if (error.request) {
          console.error('❌ No response received from server:', error.message);
        } else {
          console.error('❌ Request error:', error.message);
        }
      }
      return Promise.reject(error);
    }
  );

  return client;
}

// Export a singleton instance for convenience
export const apiClient = createApiClient();

// Export helper function to get auth token (for non-axios requests)
export function getAuthToken(): string | null {
  // Try OIDC token first
  try {
    const oidcStorage = sessionStorage.getItem(`oidc.user:${oidcConfig.authority}:${oidcConfig.client_id}`);
    if (oidcStorage) {
      const oidcUser = JSON.parse(oidcStorage);
      if (oidcUser && oidcUser.access_token) {
        return oidcUser.access_token;
      }
    }
  } catch (e) {
    if (IS_DEV) console.error('Error getting OIDC token:', e);
  }

  // Fallback to localStorage
  const token = localStorage.getItem('authToken');
  return token;
}

export { IS_DEV };
